/**
 * Filling a template's merge fields from one job.
 *
 * Separate from emailTemplates.ts because that module is about the vocabulary
 * and the ROOF.link import, and this one is about a particular job on a
 * particular day. It lives in core rather than in the component for the usual
 * reason: the preview in Tools, the send button on a job, and anything an agent
 * does through MCP must all produce the same email.
 *
 * Every value is a string. A field with no answer is absent rather than empty,
 * so `renderEmailTemplate` leaves the token visible and nobody sends a letter
 * addressed to a blank.
 */

import { EMAIL_FIELDS } from "./emailTemplates.js";
import { type Cents, formatCents } from "./money.js";
import type { Contact, Job } from "./types.js";

/** Company details, as held in the single `company_settings` row. */
export interface CompanySettings {
  name: string | null;
  phone: string | null;
  email: string | null;
  /**
   * Where a customer emails to cancel inside the three business days.
   *
   * Deliberately *not* an email template token. It is on the contract and
   * nowhere else — a mailbox watched for cancellation notices is not the
   * address to invite general replies to.
   */
  cancellation_email: string | null;
  website: string | null;
  facebook: string | null;
}

/** Money on the active estimate, already run through the ladder. */
export interface EmailTotals {
  total?: Cents | null;
  grossProfit?: Cents | null;
  margin?: number | null;
  materials?: Cents | null;
  labor?: Cents | null;
  balance?: Cents | null | undefined;
}

export interface EmailValueSources {
  job?: Partial<Job> | null;
  contact?: Partial<Contact> | null;
  /** Names, already resolved from profile ids by whoever loaded the job. */
  people?: {
    rep?: string | null;
    altRep?: string | null;
    pm?: string | null;
    coordinator?: string | null;
    inspector?: string | null;
    createdBy?: string | null;
    crew?: string | null;
    repEmail?: string | null;
    repPhone?: string | null;
  } | null;
  totals?: EmailTotals | null;
  company?: Partial<CompanySettings> | null;
  /** Whoever is about to press send. */
  sender?: {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  workOrder?: {
    number?: number | string | null;
    startDate?: string | null;
    endDate?: string | null;
  } | null;
  /** The order a `material_order` template is being sent about. */
  materialOrder?: { number?: number | string | null } | null;
  /** Where this job lives in the app, so a staff email can link to it. */
  jobUrl?: string | null;
}

/**
 * "August 3, 2026", or absent. A date nobody can read is worse than a token.
 *
 * `work_date` is a plain `date` column, and `new Date('2026-08-26')` is UTC
 * midnight — which in Alabama is the evening of the 25th. Date-only values are
 * therefore built as local dates, or half the emails would name the wrong day.
 */
function formatDate(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = dateOnly
    ? new Date(
        Number(dateOnly[1]),
        Number(dateOnly[2]) - 1,
        Number(dateOnly[3]),
      )
    : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * "$24,850.00".
 *
 * `formatCents` deliberately leaves the symbol to whoever is displaying the
 * number, and a letter to a customer is one of the places that has to supply
 * it — "Balance due: 12,425.00" is not something anybody would send. A negative
 * reads "-$100.00", not "$-100.00".
 */
function money(value: Cents | null | undefined): string | undefined {
  if (value == null) return undefined;
  const formatted = formatCents(Math.abs(value) as Cents);
  return value < 0 ? `-$${formatted}` : `$${formatted}`;
}

/** "Birmingham, AL 35242" from the parts that are present. */
function cityStateZip(
  city: string | null | undefined,
  state: string | null | undefined,
  zip: string | null | undefined,
): string | undefined {
  const left = [city, state].filter(Boolean).join(", ");
  const out = [left, zip].filter(Boolean).join(" ").trim();
  return out || undefined;
}

function fullAddress(
  address: string | null | undefined,
  city: string | null | undefined,
  state: string | null | undefined,
  zip: string | null | undefined,
): string | undefined {
  const tail = cityStateZip(city, state, zip);
  const out = [address, tail].filter(Boolean).join(", ").trim();
  return out || undefined;
}

/**
 * Split a full name for the templates that greet somebody by first name.
 *
 * "Hi {{customer.first_name}}" is the single most used field in the whole set,
 * and contacts are stored as one `full_name`, so this has to happen somewhere.
 */
function nameParts(full: string | null | undefined): {
  first?: string | undefined;
  last?: string | undefined;
} {
  const parts = (full ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return {};
  if (parts.length === 1) return { first: parts[0] };
  return { first: parts[0], last: parts[parts.length - 1] };
}

const BID_TYPE_LABELS: Record<string, string> = {
  repair: "Repair",
  retail: "Retail",
  insurance: "Insurance",
};

/**
 * Every merge value Roof Board can supply for this job.
 *
 * Keys that would be blank are left out entirely — see the note at the top.
 */
export function buildEmailValues(
  sources: EmailValueSources,
): Record<string, string> {
  const { job, contact, people, totals, company, sender } = sources;
  const { workOrder, materialOrder } = sources;
  const person = nameParts(contact?.full_name);
  const rep = nameParts(people?.rep);

  const jobType = [
    job?.job_type
      ? job.job_type[0]!.toUpperCase() + job.job_type.slice(1)
      : null,
    job?.bid_type ? (BID_TYPE_LABELS[job.bid_type] ?? job.bid_type) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const candidates: Record<string, string | number | undefined> = {
    "customer.name": contact?.full_name ?? undefined,
    "customer.first_name": person.first,
    "customer.last_name": person.last,
    "customer.email": contact?.email ?? undefined,
    "customer.phone": contact?.phone ?? undefined,
    "customer.cell": contact?.cell ?? undefined,
    "customer.address": contact?.address ?? undefined,
    "customer.city": contact?.city ?? undefined,
    "customer.state": contact?.state ?? undefined,
    "customer.zip": contact?.zip ?? undefined,

    "job.number": job?.job_number ?? undefined,
    "job.address": job?.address ?? undefined,
    "job.full_address": fullAddress(
      job?.address,
      job?.city,
      job?.state,
      job?.zip,
    ),
    "job.city_state_zip": cityStateZip(job?.city, job?.state, job?.zip),
    "job.type": jobType || undefined,
    "job.stage": job?.stage ? job.stage.replace(/_/g, " ") : undefined,
    "job.lead_source": job?.lead_source ?? undefined,
    "job.claim_number": job?.claim_number ?? undefined,
    "job.url": sources.jobUrl ?? undefined,
    "job.inspection_date": formatDate(job?.inspection_at),
    "job.work_date": formatDate(job?.work_date),
    "job.signed_date": formatDate(job?.signed_contract_at ?? job?.sends_at),

    "rep.name": people?.rep ?? undefined,
    "rep.first_name": rep.first,
    "rep.last_name": rep.last,
    "rep.email": people?.repEmail ?? undefined,
    "rep.phone": people?.repPhone ?? undefined,
    "alt_rep.name": people?.altRep ?? undefined,
    "pm.name": people?.pm ?? undefined,
    "coordinator.name": people?.coordinator ?? undefined,
    "inspector.name": people?.inspector ?? undefined,
    "crew.name": people?.crew ?? undefined,
    "created_by.name": people?.createdBy ?? undefined,

    "estimate.total": money(totals?.total),
    "estimate.gross_profit": money(totals?.grossProfit),
    "estimate.margin":
      totals?.margin == null
        ? undefined
        : `${(totals.margin * 100).toFixed(1)}%`,
    "estimate.materials": money(totals?.materials),
    "estimate.labor": money(totals?.labor),
    "estimate.balance": money(totals?.balance),

    "material_order.number": materialOrder?.number ?? undefined,
    "work_order.number": workOrder?.number ?? undefined,
    "work_order.start_date": formatDate(workOrder?.startDate),
    "work_order.end_date": formatDate(workOrder?.endDate),

    "company.name": company?.name ?? undefined,
    "company.phone": company?.phone ?? undefined,
    "company.email": company?.email ?? undefined,
    "company.website": company?.website ?? undefined,
    "company.facebook": company?.facebook ?? undefined,

    "sender.name": sender?.name ?? undefined,
    "sender.email": sender?.email ?? undefined,
    "sender.phone": sender?.phone ?? undefined,
  };

  const values: Record<string, string> = {};
  for (const [token, value] of Object.entries(candidates)) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) values[token] = text;
  }
  return values;
}

/**
 * Made-up values for every field, for the preview on the Emails screen.
 *
 * A template is edited long before it is sent, so the editor needs something to
 * show. Obvious fakes rather than a real customer's details: nobody should be
 * able to mistake the preview for a job.
 */
export function sampleEmailValues(): Record<string, string> {
  const sample: Record<string, string> = {
    "customer.name": "Dale Whitaker",
    "customer.first_name": "Dale",
    "customer.last_name": "Whitaker",
    "customer.email": "dale.whitaker@example.com",
    "customer.phone": "(205) 555-0148",
    "customer.cell": "(205) 555-0172",
    "customer.address": "418 Sample Lane",
    "customer.city": "Birmingham",
    "customer.state": "AL",
    "customer.zip": "35242",
    "job.number": "4014911",
    "job.address": "418 Sample Lane",
    "job.full_address": "418 Sample Lane, Birmingham, AL 35242",
    "job.city_state_zip": "Birmingham, AL 35242",
    "job.type": "Residential · Insurance",
    "job.stage": "in production",
    "job.lead_source": "Referral",
    "job.claim_number": "CLM-000-EXAMPLE",
    "job.url": "https://example.invalid/jobs/sample",
    "job.inspection_date": "August 12, 2026",
    "job.work_date": "August 26, 2026",
    "job.signed_date": "August 14, 2026",
    "rep.name": "Sample Rep",
    "rep.first_name": "Sample",
    "rep.last_name": "Rep",
    "rep.email": "rep@example.com",
    "rep.phone": "(205) 555-0100",
    "alt_rep.name": "Second Rep",
    "pm.name": "Sample Manager",
    "coordinator.name": "Sample Coordinator",
    "inspector.name": "Sample Inspector",
    "crew.name": "Crew One",
    "created_by.name": "Sample Rep",
    "material_order.number": "3312",
    "estimate.total": "$24,850.00",
    "estimate.gross_profit": "$9,940.00",
    "estimate.margin": "40.0%",
    "estimate.materials": "$8,410.00",
    "estimate.labor": "$6,500.00",
    "estimate.balance": "$12,425.00",
    "work_order.number": "118",
    "work_order.start_date": "August 26, 2026",
    "work_order.end_date": "August 27, 2026",
    "company.name": "Your company",
    "company.phone": "(205) 555-0000",
    "company.email": "office@example.com",
    "company.website": "www.example.com",
    "company.facebook": "facebook.com/example",
    "sender.name": "You",
    "sender.email": "you@example.com",
    "sender.phone": "(205) 555-0000",
  };

  // A field added to EMAIL_FIELDS without a sample here would preview as a raw
  // token and look broken; fall back to something obviously fake instead.
  for (const field of EMAIL_FIELDS) {
    sample[field.token] ??= `[${field.label}]`;
  }
  return sample;
}
